import { createFileRoute, redirect } from "@tanstack/react-router";
import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Button as UIButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

type StoryRenderer = (props: Record<string, unknown>) => React.ReactNode;

type StoryKnobBase = {
  prop: string;
  label?: string;
  description?: string;
};

type StoryTextKnob = StoryKnobBase & {
  type: "text";
  placeholder?: string;
  defaultValue?: string;
};

type StoryBooleanKnob = StoryKnobBase & {
  type: "boolean";
  defaultValue?: boolean;
};

type StoryNumberKnob = StoryKnobBase & {
  type: "number";
  defaultValue?: number;
  step?: number;
  min?: number;
  max?: number;
};

type StorySelectKnob = StoryKnobBase & {
  type: "select";
  options: Array<{
    label: string;
    value: string;
  }>;
  defaultValue?: string;
};

type StoryKnob = StoryTextKnob | StoryBooleanKnob | StoryNumberKnob | StorySelectKnob;

type StoryDefinition = {
  id?: string;
  uid?: string;
  title: string;
  description?: string;
  render: StoryRenderer;
  initialProps?: Record<string, unknown>;
  knobs?: StoryKnob[];
};

type StoryGroup = {
  id?: string;
  uid?: string;
  title: string;
  description?: string;
  stories: StoryDefinition[];
};

const storyModules = import.meta.glob("./-stories/**/*.stories.{ts,tsx}", {
  eager: true,
});

export const Route = createFileRoute("/storyboard/")({
  beforeLoad: ({ context }) => {
    if (!context.auth.loading && !context.auth.profileLoading) {
      if (
        !context.auth.user ||
        ["admin", "manager"].indexOf(context.auth.userProfile?.type ?? "") === -1
      ) {
        throw redirect({
          to: "/login",
        });
      }
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const storyGroups = React.useMemo(() => loadStoryGroups(), []);
  const allStories = React.useMemo(
    () => storyGroups.flatMap((group) => group.stories),
    [storyGroups],
  );
  const defaultStoryUid = allStories[0]?.uid ?? "";
  const [selectedStoryUid, setSelectedStoryUid] = React.useState(defaultStoryUid);

  const selectedStory = React.useMemo(
    () => allStories.find((story) => story.uid === selectedStoryUid) ?? allStories[0],
    [allStories, selectedStoryUid],
  );

  const [knobState, setKnobState] = React.useState<Record<string, unknown>>(() =>
    buildKnobState(selectedStory),
  );

  // State for collapsed/expanded groups (first group open by default)
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(() => {
    const firstGroupId = storyGroups[0]?.uid ?? storyGroups[0]?.id ?? "";
    return new Set([firstGroupId]);
  });

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const expandAll = () => {
    const allGroupIds = storyGroups.map((group) => group.uid ?? group.id ?? group.title);
    setExpandedGroups(new Set(allGroupIds));
  };

  const toggleAllGroups = () => {
    if (expandedGroups.size === 0) {
      expandAll();
    } else {
      collapseAll();
    }
  };

  React.useEffect(() => {
    setKnobState(buildKnobState(selectedStory));
  }, [selectedStory?.uid]);

  React.useEffect(() => {
    if (!selectedStoryUid && defaultStoryUid) {
      setSelectedStoryUid(defaultStoryUid);
    }
  }, [defaultStoryUid, selectedStoryUid]);

  React.useEffect(() => {
    if (selectedStoryUid && !selectedStory && defaultStoryUid) {
      setSelectedStoryUid(defaultStoryUid);
    }
  }, [defaultStoryUid, selectedStory, selectedStoryUid]);

  const storyProps = React.useMemo(() => {
    if (!selectedStory) {
      return {} as Record<string, unknown>;
    }

    return {
      ...(selectedStory.initialProps ?? {}),
      ...knobState,
    };
  }, [selectedStory, knobState]);

  const storyContent = React.useMemo(() => {
    if (!selectedStory) {
      return <span className="text-muted-foreground">Pick a story to see it here.</span>;
    }

    return selectedStory.render(storyProps);
  }, [selectedStory, storyProps]);

  if (!storyGroups.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
        <p className="text-lg font-semibold">No stories found</p>
        <p className="text-muted-foreground max-w-md text-sm">
          Add story files to{" "}
          <code className="bg-muted rounded px-2 py-1">
            src/routes/dashboard/storyboard/-stories
          </code>{" "}
          to populate this storyboard.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-muted/40 flex h-screen flex-col overflow-hidden">
      <div className="flex flex-1 gap-2 overflow-hidden p-6 lg:flex-row">
        <aside className="flex h-full flex-col overflow-hidden lg:w-64">
          <div className="flex-shrink-0 pb-4">
            <div className="mb-2 flex items-center justify-between">
              <h1 className="text-lg font-semibold">Storyboard</h1>
              <UIButton
                variant="ghost"
                size="sm"
                onClick={toggleAllGroups}
                className="h-7 px-2 text-xs"
              >
                {expandedGroups.size === 0 ? "Expand All" : "Collapse All"}
              </UIButton>
            </div>
            <p className="text-muted-foreground text-sm">
              Browse component stories and tweak their knobs.
            </p>
          </div>

          <nav
            className="flex-1 space-y-2 overflow-y-auto pr-3 text-sm"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#d1d5db transparent",
            }}
          >
            {storyGroups.map((group) => {
              const groupId = group.uid ?? group.id ?? group.title;
              const isExpanded = expandedGroups.has(groupId);

              return (
                <div key={groupId} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupId)}
                    className="hover:bg-muted/80 flex w-full items-center justify-between rounded-md px-3 py-2 text-left transition"
                  >
                    <span className="text-foreground text-xs font-semibold tracking-wide uppercase">
                      {group.title}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="text-muted-foreground h-4 w-4" />
                    ) : (
                      <ChevronRight className="text-muted-foreground h-4 w-4" />
                    )}
                  </button>

                  {isExpanded && (
                    <ul className="space-y-1 pl-2">
                      {group.stories.map((story) => {
                        const isActive = story.uid === selectedStory?.uid;
                        return (
                          <li key={story.uid}>
                            <button
                              type="button"
                              onClick={() => setSelectedStoryUid(story.uid ?? "")}
                              className={cn(
                                "focus-visible:ring-ring w-full rounded-md px-3 py-2 text-left text-sm transition focus-visible:ring-2 focus-visible:outline-none",
                                isActive
                                  ? "bg-primary text-primary-foreground shadow-sm"
                                  : "hover:bg-muted/80 text-foreground",
                              )}
                            >
                              <div className="font-medium">{story.title}</div>
                              {story.description ? (
                                <div
                                  className={`text-xs ${isActive ? "text-primary-foreground/90" : "text-muted-foreground"}`}
                                >
                                  {story.description}
                                </div>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <div className="w-4 flex-shrink-0"></div>

        <main
          className="flex-1 overflow-y-auto pr-4"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#d1d5db transparent",
          }}
        >
          <div className="space-y-6">
            <section className="space-y-2">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">
                  {selectedStory?.title ?? "Select a story"}
                </h2>
                {selectedStory?.description ? (
                  <p className="text-muted-foreground text-sm">{selectedStory.description}</p>
                ) : null}
              </div>
            </section>

            <div
              className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]"
              style={{ height: "calc(100vh - 200px)" }}
            >
              <div
                key={selectedStory?.uid ?? "preview"}
                className="bg-background flex h-full items-center justify-center rounded-lg border p-8 shadow-sm"
              >
                <div className="flex w-full max-w-md justify-center">{storyContent}</div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
                    Knobs
                  </h3>
                  <UIButton
                    variant="outline"
                    size="sm"
                    onClick={() => setKnobState(buildKnobState(selectedStory))}
                    disabled={!selectedStory?.knobs?.length}
                  >
                    Reset
                  </UIButton>
                </div>

                <div className="bg-background rounded-lg border p-4 shadow-sm">
                  {selectedStory?.knobs?.length ? (
                    <div className="space-y-4">
                      {selectedStory.knobs.map((knob) => {
                        const knobId = `${selectedStory.uid ?? "story"}-${knob.prop}`;
                        const value = knobState[knob.prop];

                        if (knob.type === "text") {
                          return (
                            <div key={knobId} className="space-y-1">
                              <Label htmlFor={knobId}>{knob.label ?? knob.prop}</Label>
                              <Input
                                id={knobId}
                                type="text"
                                value={
                                  typeof value === "string"
                                    ? value
                                    : value != null
                                      ? String(value)
                                      : ""
                                }
                                placeholder={knob.placeholder}
                                onChange={(event) =>
                                  setKnobState((current) => ({
                                    ...current,
                                    [knob.prop]: event.target.value,
                                  }))
                                }
                              />
                              {knob.description ? (
                                <p className="text-muted-foreground text-xs">{knob.description}</p>
                              ) : null}
                            </div>
                          );
                        }

                        if (knob.type === "number") {
                          return (
                            <div key={knobId} className="space-y-1">
                              <Label htmlFor={knobId}>{knob.label ?? knob.prop}</Label>
                              <Input
                                id={knobId}
                                type="number"
                                value={
                                  typeof value === "number"
                                    ? value
                                    : value === "" || value == null
                                      ? ""
                                      : Number(value)
                                }
                                onChange={(event) =>
                                  setKnobState((current) => ({
                                    ...current,
                                    [knob.prop]:
                                      event.target.value === "" ? "" : Number(event.target.value),
                                  }))
                                }
                                step={knob.step}
                                min={knob.min}
                                max={knob.max}
                              />
                              {knob.description ? (
                                <p className="text-muted-foreground text-xs">{knob.description}</p>
                              ) : null}
                            </div>
                          );
                        }

                        if (knob.type === "boolean") {
                          return (
                            <div
                              key={knobId}
                              className="flex items-center justify-between gap-4 rounded-md border px-4 py-3"
                            >
                              <div className="flex-1 space-y-1">
                                <Label htmlFor={knobId} className="text-sm font-medium">
                                  {knob.label ?? knob.prop}
                                </Label>
                                {knob.description ? (
                                  <p className="text-muted-foreground text-xs">
                                    {knob.description}
                                  </p>
                                ) : null}
                              </div>
                              <Switch
                                id={knobId}
                                checked={Boolean(value)}
                                onCheckedChange={(checked) =>
                                  setKnobState((current) => ({
                                    ...current,
                                    [knob.prop]: checked,
                                  }))
                                }
                              />
                            </div>
                          );
                        }

                        if (knob.type === "select") {
                          return (
                            <div key={knobId} className="space-y-1">
                              <Label htmlFor={knobId}>{knob.label ?? knob.prop}</Label>
                              <select
                                id={knobId}
                                className="border-input focus-visible:ring-ring/50 flex h-9 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition outline-none hover:cursor-pointer focus-visible:ring-[3px]"
                                value={
                                  typeof value === "string"
                                    ? value
                                    : (knob.defaultValue ?? knob.options[0]?.value ?? "")
                                }
                                onChange={(event) =>
                                  setKnobState((current) => ({
                                    ...current,
                                    [knob.prop]: event.target.value,
                                  }))
                                }
                              >
                                {knob.options.map((option) => (
                                  <option key={`${knobId}-${option.value}`} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {knob.description ? (
                                <p className="text-muted-foreground text-xs">{knob.description}</p>
                              ) : null}
                            </div>
                          );
                        }

                        return null;
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      This story does not expose any knobs.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function buildKnobState(story?: StoryDefinition | null) {
  if (!story) {
    return {};
  }

  const state: Record<string, unknown> = story.initialProps ? { ...story.initialProps } : {};

  for (const knob of story.knobs ?? []) {
    if (knob.type === "text") {
      if (!(knob.prop in state)) {
        state[knob.prop] = knob.defaultValue ?? "";
      } else if (state[knob.prop] != null) {
        state[knob.prop] = String(state[knob.prop]);
      }
      continue;
    }

    if (knob.type === "boolean") {
      if (!(knob.prop in state)) {
        state[knob.prop] = knob.defaultValue ?? false;
      } else {
        state[knob.prop] = Boolean(state[knob.prop]);
      }
      continue;
    }

    if (knob.type === "number") {
      if (!(knob.prop in state)) {
        state[knob.prop] = knob.defaultValue ?? 0;
      } else if (typeof state[knob.prop] !== "number") {
        const parsed = Number(state[knob.prop]);
        state[knob.prop] = Number.isNaN(parsed) ? (knob.defaultValue ?? 0) : parsed;
      }
      continue;
    }

    if (knob.type === "select") {
      if (!(knob.prop in state)) {
        state[knob.prop] = knob.defaultValue ?? knob.options[0]?.value ?? "";
      }
      continue;
    }
  }

  return state;
}

function loadStoryGroups(): StoryGroup[] {
  return Object.entries(storyModules).flatMap(([path, moduleExports]) => {
    const exported = resolveModuleExport(moduleExports);

    if (!exported) {
      return [];
    }

    if (Array.isArray(exported)) {
      if (exported.every(isStoryGroup)) {
        return exported.map((group, index) => normalizeGroup(group, path, index));
      }

      if (exported.every(isStoryDefinition)) {
        return [normalizeGroup({ title: deriveTitleFromPath(path), stories: exported }, path, 0)];
      }

      return [];
    }

    if (isStoryGroup(exported)) {
      return [normalizeGroup(exported, path, 0)];
    }

    return [];
  });
}

function resolveModuleExport(moduleExports: unknown) {
  if (isRecord(moduleExports) && "default" in moduleExports) {
    const defaultExport = (moduleExports as Record<string, unknown>).default;
    if (defaultExport != null) {
      return defaultExport;
    }
  }

  return moduleExports;
}

function normalizeGroup(rawGroup: StoryGroup, path: string, index: number): StoryGroup {
  const title = rawGroup.title || deriveTitleFromPath(path);
  const id = rawGroup.id ?? slugify(`${title}-${index}`);
  const stories = (rawGroup.stories ?? []).map((story, storyIndex) =>
    normalizeStory(story, id, storyIndex),
  );

  return {
    ...rawGroup,
    title,
    id,
    uid: rawGroup.uid ?? id,
    stories,
  };
}

function normalizeStory(
  rawStory: StoryDefinition,
  groupId: string,
  index: number,
): StoryDefinition {
  const title = rawStory.title || `Story ${index + 1}`;
  const id = rawStory.id ?? slugify(`${groupId}-${title}`);
  const uid = rawStory.uid ?? `${groupId}:${id}`;

  return {
    ...rawStory,
    title,
    id,
    uid,
    initialProps: rawStory.initialProps ? { ...rawStory.initialProps } : undefined,
    knobs: rawStory.knobs ? rawStory.knobs.map((knob) => ({ ...knob })) : undefined,
  };
}

function deriveTitleFromPath(path: string) {
  const filename = path.split("/").pop() ?? "Story";
  const withoutExtension = filename.replace(/\.stories\.[tj]sx?$/, "");
  const cleaned = withoutExtension.replace(/[-_]+/g, " ");
  const titled = cleaned
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return titled || "Story";
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "")
      .replace(/-{2,}/g, "-") || "story"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isStoryGroup(value: unknown): value is StoryGroup {
  if (!isRecord(value)) {
    return false;
  }

  return Array.isArray((value as StoryGroup).stories);
}

function isStoryDefinition(value: unknown): value is StoryDefinition {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof (value as StoryDefinition).title === "string" &&
    typeof (value as StoryDefinition).render === "function"
  );
}

export type { StoryDefinition, StoryGroup, StoryKnob };
